import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  notifications as notificationsApi,
} from "@/lib/api";

/**
 * `/settings` — user-level preferences.
 *
 * For now: the email-notification toggle. Future settings (display name,
 * default doc visibility, etc.) belong here too.
 */
export function Settings() {
  const qc = useQueryClient();

  const prefsQuery = useQuery({
    queryKey: ["notification_preferences"],
    queryFn: () => notificationsApi.getPreferences(),
  });

  const updateMut = useMutation({
    mutationFn: (enabled: boolean) => notificationsApi.setPreferences(enabled),
    onSuccess: (res) => {
      qc.setQueryData(["notification_preferences"], res);
    },
  });

  const enabled = prefsQuery.data?.notifications_enabled ?? false;
  const email = prefsQuery.data?.email ?? "";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <header className="mb-6">
        <Link
          to="/"
          className="text-sm text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
        >
          ← Coauthor
        </Link>
        <h1 className="text-2xl font-semibold mt-1">Settings</h1>
      </header>

      <section className="rounded-md border border-zinc-200 dark:border-zinc-800 p-4 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-medium">Email notifications</h2>
            <p className="text-sm text-zinc-500 mt-1">
              We'll email you when someone comments on a document you own or
              contribute to, or when a cross-vendor model review lands on
              one of those documents. Other activity (edits, status changes)
              doesn't generate emails.
            </p>
            {prefsQuery.isLoading ? (
              <div className="text-sm text-zinc-500 mt-3">Loading…</div>
            ) : prefsQuery.isError ? (
              <div className="text-sm text-red-600 dark:text-red-400 mt-3">
                {(prefsQuery.error as ApiError).body
                  ? (
                      (prefsQuery.error as ApiError).body as { error?: string }
                    ).error
                  : String(prefsQuery.error)}
              </div>
            ) : (
              <div className="text-sm text-zinc-500 mt-3">
                Sending to <span className="font-mono">{email}</span>
              </div>
            )}
          </div>

          <label className="inline-flex items-center gap-2 flex-shrink-0 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              disabled={prefsQuery.isLoading || updateMut.isPending}
              onChange={(e) => updateMut.mutate(e.target.checked)}
              className="h-4 w-4 rounded border-zinc-300 dark:border-zinc-700"
            />
            <span className="text-sm">{enabled ? "On" : "Off"}</span>
          </label>
        </div>
        {updateMut.error && (
          <div className="text-xs text-red-600 dark:text-red-400 mt-3">
            {(updateMut.error as ApiError).body
              ? (
                  (updateMut.error as ApiError).body as { error?: string }
                ).error
              : String(updateMut.error)}
          </div>
        )}
      </section>

      <section className="rounded-md border border-zinc-200 dark:border-zinc-800 p-4">
        <h2 className="text-base font-medium">Other</h2>
        <p className="text-sm text-zinc-500 mt-1">
          More settings will live here. For agent tokens, see{" "}
          <Link to="/tokens" className="underline">
            /tokens
          </Link>
          .
        </p>
      </section>
    </div>
  );
}
